/**
 * LGPD-safe param sanitizer for analytics events.
 *
 * Strips PII (CPF, CNPJ, email, phone) and forbidden keys (password, token).
 * Truncates long strings and caps array sizes so events stay under Firebase's
 * 100-param / 100-char limits.
 */

const FORBIDDEN_KEYS = new Set([
  'password',
  'pwd',
  'senha',
  'token',
  'authorization',
  'jwt',
  'cpf',
  'cnpj',
  'document',
  'documentNumber',
  'email',
  'phone',
  'cellphone',
  'celular',
  'telefone',
  'birthdate',
  'address',
]);

const CPF_RE = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/;
const CNPJ_RE = /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/;
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/;
const PHONE_RE = /\b(\+?\d{2}\s?)?\(?\d{2}\)?\s?9?\d{4}-?\d{4}\b/;

const looksLikePII = (value: string): boolean =>
  CPF_RE.test(value) || CNPJ_RE.test(value) || EMAIL_RE.test(value) || PHONE_RE.test(value);

const truncate = (value: string, max = 100): string =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

const sanitizeValue = (value: unknown): unknown => {
  if (value == null) return value;
  if (typeof value === 'string') {
    if (looksLikePII(value)) return '[redacted]';
    return truncate(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'object') return Object.keys(value as object).length;
  return undefined;
};

export const sanitizeParams = (
  params?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  if (!params) return undefined;
  const out: Record<string, unknown> = {};
  let count = 0;
  for (const [key, value] of Object.entries(params)) {
    if (count >= 25) break;
    if (FORBIDDEN_KEYS.has(key.toLowerCase())) continue;
    const sanitized = sanitizeValue(value);
    if (sanitized === undefined) continue;
    out[key] = sanitized;
    count += 1;
  }
  return out;
};
